import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import RFIDLoginPage from "./pages/RFIDLoginPage";
import DashboardPage from "./pages/DashboardPage";
import BlueprintsPage from "./pages/BlueprintsPage";
import MachinesPage from "./pages/MachinesPage";
import MachineReferencePage from "./pages/MachineReferencePage";
import MaintenancePage from "./pages/MaintenancePage";
import MaintenanceCalendarPage from "./pages/MaintenanceCalendarPage";
import NetworkScanPage from "./pages/NetworkScanPage";
import QRScannerPage from "./pages/QRScannerPage";
import UserManagementPage from "./pages/UserManagementPage";
import PartsPage from "./pages/PartsPage";
import LDAPConfigPage from "./pages/LDAPConfigPage";
import CategorySettingsPage from "./pages/CategorySettingsPage";
import BrandingSettingsPage from "./pages/BrandingSettingsPage";
import SAPIntegrationPage from "./pages/SAPIntegrationPage";
import WorkOrdersPage from "./pages/WorkOrdersPage";
import TeamsConfigPage from "./pages/TeamsConfigPage";
import SharePointPage from "./pages/SharePointPage";
import M365HubPage from "./pages/M365HubPage";
import QMSConfigPage from "./pages/QMSConfigPage";
import AVEDAPIPage from "./pages/AVEDAPIPage";
import PowerAutomatePage from "./pages/PowerAutomatePage";
import PrimaveraPage from "./pages/PrimaveraPage";
import SchedulerDashboardPage from "./pages/SchedulerDashboardPage";
import AboutPage from "./pages/AboutPage";
import ConnectorsListPage from "./pages/ConnectorsListPage";
import ConnectorPage from "./pages/ConnectorPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import CompliancePage from "./pages/CompliancePage";
import OPCUAGatewayPage from "./pages/OPCUAGatewayPage";
import ForgeViewerPage from "./pages/ForgeViewerPage";
import CustomizationPage from "./pages/CustomizationPage";
import DashboardBuilderPage from "./pages/DashboardBuilderPage";
import NavBuilderPage from "./pages/NavBuilderPage";
import ECMPage from "./pages/ECMPage";
import ConfigMgmtPage from "./pages/ConfigMgmtPage";
import RetentionPage from "./pages/RetentionPage";
import ProceduresPage from "./pages/ProceduresPage";
import CAPPage from "./pages/CAPPage";
import QualificationsPage from "./pages/QualificationsPage";
import SupplierQualityPage from "./pages/SupplierQualityPage";
import AuditMgmtPage from "./pages/AuditMgmtPage";
import AISearchPage from "./pages/AISearchPage";
import KnowledgeGraphPage from "./pages/KnowledgeGraphPage";
import PredictivePage from "./pages/PredictivePage";
import KPICenterPage from "./pages/KPICenterPage";
import ComplianceHubPage from "./pages/ComplianceHubPage";
import DigitalTwinPage from "./pages/DigitalTwinPage";
import MarketplacePage from "./pages/MarketplacePage";
import MyWorkPage from "./pages/MyWorkPage";
import SystemAdminPage from "./pages/SystemAdminPage";
import { useSessionTimeout, SessionTimeoutWarning } from "./components/shared/SessionTimeout";
import { AppSettingsProvider } from "./contexts/AppSettingsContext";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

// Store token as fallback when cookies don't work (cross-origin production)
let _authToken = null;
export const setAuthToken = (token) => { _authToken = token; };

// Auth headers helper - sends Bearer token as fallback for cookies
export const getAuthHeaders = () => _authToken ? { Authorization: `Bearer ${_authToken}` } : {};

// Axios request interceptor: attach Bearer token as fallback
axios.interceptors.request.use((config) => {
  if (_authToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_authToken}`;
  }
  return config;
});

// Axios response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/register") &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/badge-scan") &&
      !originalRequest.url?.includes("/auth/rfid") &&
      !originalRequest.url?.includes("/auth/me")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API}/auth/refresh`);
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        window.dispatchEvent(new Event("auth:expired"));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Register service worker for PWA
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  });
}

function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (!offline) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#DC2626] text-white text-center py-1 text-sm font-bold tracking-wide" data-testid="offline-banner">
      OFFLINE — Some features may be unavailable
    </div>
  );
}

// ⚠️ DEMO MODE — Set to true to bypass login with full admin access. Set to false for production.
const DEMO_MODE = true;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalToggles, setGlobalToggles] = useState({});
  const [machineCategories, setMachineCategories] = useState([]);

  const fetchToggles = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/feature-toggles`);
      setGlobalToggles(resp.data.toggles || {});
    } catch { /* default all enabled */ }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/machine-categories`);
      setMachineCategories(resp.data || []);
    } catch { /* default empty */ }
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      // Demo mode: auto-login as admin
      if (DEMO_MODE) {
        try {
          const scanResp = await axios.post(`${API}/auth/badge-scan`, { employee_number: "10001" });
          if (scanResp.data?.token) setAuthToken(scanResp.data.token);
          const response = await axios.get(`${API}/auth/me`, {
            headers: scanResp.data?.token ? { Authorization: `Bearer ${scanResp.data.token}` } : {},
          });
          if (response.data) {
            setUser(response.data);
            setIsAuthenticated(true);
          }
        } catch {
          setUser({ id: "demo", name: "Demo Admin", role: "admin", employee_number: "10001" });
          setIsAuthenticated(true);
        }
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    verifyAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchToggles();
  }, [isAuthenticated, fetchToggles]);

  useEffect(() => {
    if (isAuthenticated) fetchCategories();
  }, [isAuthenticated, fetchCategories]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    setUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const onExpired = () => handleLogout();
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if a feature is enabled for the current user
  const isFeatureEnabled = useCallback((featureKey) => {
    // Global toggle OFF → disabled for everyone
    if (globalToggles[featureKey] === false) return false;
    // Per-user override
    const userAccess = user?.custom_access;
    if (userAccess && userAccess[featureKey] !== undefined) return userAccess[featureKey];
    // Default: enabled
    return true;
  }, [globalToggles, user]);

  // Session timeout (15 min idle) — disabled in DEMO_MODE
  const { showWarning, countdown, extendSession } = useSessionTimeout(handleLogout, isAuthenticated && !DEMO_MODE);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <div className="bg-[#0C0A09] h-11 flex items-center px-6">
          <span className="text-white font-heading font-bold text-sm tracking-wider">VAULT</span>
        </div>
      </div>
    );
  }

  const userRole = user?.role || "hourly";
  const isAdmin = userRole === "admin";
  const isSuperOrAbove = userRole === "admin" || userRole === "superintendent";
  const isTechOrAbove = isSuperOrAbove || userRole === "technician";

  // Route access: role + feature toggle
  const canAccess = (featureKey, roleCheck = true) => roleCheck && isFeatureEnabled(featureKey);

  // Default category for /machines redirect
  const defaultCategorySlug = machineCategories.length > 0 ? machineCategories[0].name.toLowerCase() : "all";

  return (
    <AppSettingsProvider isAuthenticated={isAuthenticated}>
      <div className="App">
        <OfflineBanner />
        {showWarning && <SessionTimeoutWarning countdown={countdown} onExtend={extendSession} />}
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="/rfid-login" element={<Navigate to="/login" />} />
            <Route path="/" element={isAuthenticated ? <DashboardPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/login" />} />
            <Route path="/blueprints" element={isAuthenticated ? (canAccess("blueprints") ? <BlueprintsPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />

            {/* Dynamic machine category routes */}
            {machineCategories.map((cat) => (
              <Route
                key={cat.name}
                path={`/machines/${cat.name.toLowerCase()}`}
                element={
                  isAuthenticated
                    ? (canAccess(`cat_${cat.name}`)
                      ? <MachinesPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} category={cat.name} machineCategories={machineCategories} />
                      : <Navigate to="/" />)
                    : <Navigate to="/login" />
                }
              />
            ))}
            <Route path="/machines" element={<Navigate to={`/machines/${defaultCategorySlug}`} />} />

            <Route path="/machine-index" element={isAuthenticated ? (canAccess("machine_index") ? <MachineReferencePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} machineCategories={machineCategories} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/machine-reference" element={<Navigate to="/machine-index" />} />
            <Route path="/maintenance" element={isAuthenticated ? (canAccess("maintenance", isTechOrAbove) ? <MaintenancePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/work-orders" element={isAuthenticated ? (canAccess("work_orders") ? <WorkOrdersPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/maintenance/calendar" element={isAuthenticated ? (canAccess("maintenance_calendar", isTechOrAbove) ? <MaintenanceCalendarPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/network-scan" element={isAuthenticated ? (canAccess("network_scan", isTechOrAbove) ? <NetworkScanPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/qr-scanner" element={isAuthenticated ? (canAccess("qr_scanner") ? <QRScannerPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/parts" element={isAuthenticated ? (canAccess("parts_catalog", isTechOrAbove) ? <PartsPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/users" element={isAuthenticated ? (isAdmin ? <UserManagementPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} machineCategories={machineCategories} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/ldap-config" element={isAuthenticated ? (isAdmin ? <LDAPConfigPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/machine-settings" element={isAuthenticated ? (isAdmin ? <CategorySettingsPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} onCategoriesChanged={fetchCategories} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/branding" element={isAuthenticated ? (isAdmin ? <BrandingSettingsPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/sap-integration" element={isAuthenticated ? (isAdmin ? <SAPIntegrationPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/teams-config" element={isAuthenticated ? (isAdmin ? <TeamsConfigPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/sharepoint" element={isAuthenticated ? (isAdmin ? <SharePointPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/m365" element={isAuthenticated ? (isAdmin ? <M365HubPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/opcua" element={isAuthenticated ? (isAdmin ? <OPCUAGatewayPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/forge-viewer" element={isAuthenticated ? (isAdmin ? <ForgeViewerPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/customization" element={isAuthenticated ? (isAdmin ? <CustomizationPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/dashboard-builder" element={isAuthenticated ? (isAdmin ? <DashboardBuilderPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/nav-builder" element={isAuthenticated ? (isAdmin ? <NavBuilderPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/qms" element={isAuthenticated ? (isAdmin ? <QMSConfigPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/aveva-pi" element={isAuthenticated ? (isAdmin ? <AVEDAPIPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/power-automate" element={isAuthenticated ? (isAdmin ? <PowerAutomatePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/primavera" element={isAuthenticated ? (isAdmin ? <PrimaveraPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/schedulers" element={isAuthenticated ? (isAdmin ? <SchedulerDashboardPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/about" element={isAuthenticated ? <AboutPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/login" />} />
            <Route path="/connectors" element={isAuthenticated ? (isAdmin ? <ConnectorsListPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/connectors/:connectorKey" element={isAuthenticated ? (isAdmin ? <ConnectorPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/audit-trail" element={isAuthenticated ? (isSuperOrAbove ? <AuditTrailPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/ecm" element={isAuthenticated ? (canAccess("ecm", isTechOrAbove) ? <ECMPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/config-mgmt" element={isAuthenticated ? (canAccess("config_mgmt", isTechOrAbove) ? <ConfigMgmtPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/retention" element={isAuthenticated ? (canAccess("retention", isTechOrAbove) ? <RetentionPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/procedures" element={isAuthenticated ? (canAccess("procedures", isTechOrAbove) ? <ProceduresPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/cap" element={isAuthenticated ? (canAccess("cap", isTechOrAbove) ? <CAPPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/qualifications" element={isAuthenticated ? (canAccess("qualifications", isTechOrAbove) ? <QualificationsPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/supplier-quality" element={isAuthenticated ? (canAccess("supplier_quality", isTechOrAbove) ? <SupplierQualityPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/audit-mgmt" element={isAuthenticated ? (canAccess("audit_mgmt", isTechOrAbove) ? <AuditMgmtPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/ai-search" element={isAuthenticated ? (canAccess("ai_search", isTechOrAbove) ? <AISearchPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/knowledge-graph" element={isAuthenticated ? (canAccess("knowledge_graph", isTechOrAbove) ? <KnowledgeGraphPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/predictive" element={isAuthenticated ? (canAccess("predictive", isTechOrAbove) ? <PredictivePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/kpi-center" element={isAuthenticated ? (canAccess("kpi_center", true) ? <KPICenterPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/compliance-hub" element={isAuthenticated ? (canAccess("compliance_hub", isTechOrAbove) ? <ComplianceHubPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/digital-twin" element={isAuthenticated ? (canAccess("digital_twin", isTechOrAbove) ? <DigitalTwinPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/marketplace" element={isAuthenticated ? (canAccess("marketplace", true) ? <MarketplacePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/my-work" element={isAuthenticated ? <MyWorkPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/login" />} />
            <Route path="/system-admin" element={isAuthenticated ? (userRole === "admin" ? <SystemAdminPage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/compliance" element={isAuthenticated ? <CompliancePage onLogout={handleLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled} /> : <Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AppSettingsProvider>
  );
}

export default App;
